// Shared helpers for the /grants section.

export type ProgramType =
  | "grant"
  | "seed_fund"
  | "gov_scheme"
  | "fellowship"
  | "hackathon"
  | "pitch_competition"
  | "challenge"
  | "accelerator"
  | "software_credits"
  | "prize"
  | "crowdfunding"
  | "remote_cohort_course";

export const PROGRAM_TYPES: { value: ProgramType; label: string }[] = [
  { value: "grant", label: "Grant" },
  { value: "seed_fund", label: "Seed fund" },
  { value: "gov_scheme", label: "Gov scheme" },
  { value: "fellowship", label: "Fellowship" },
  { value: "hackathon", label: "Hackathon" },
  { value: "pitch_competition", label: "Pitch competition" },
  { value: "challenge", label: "Challenge" },
  { value: "accelerator", label: "Accelerator" },
  { value: "software_credits", label: "Software credits" },
  { value: "prize", label: "Prize" },
  { value: "crowdfunding", label: "Crowdfunding" },
  { value: "remote_cohort_course", label: "Free remote cohort" },
];

export function programTypeLabel(t: string | null | undefined): string {
  if (!t) return "Program";
  const hit = PROGRAM_TYPES.find((p) => p.value === t);
  return hit ? hit.label : t.replace(/_/g, " ");
}

// tone: which chip color to use per program type
export function programTypeTone(
  t: string | null | undefined,
): "lavender" | "mint" | "peach" {
  switch (t) {
    case "seed_fund":
    case "accelerator":
    case "prize":
      return "peach";
    case "hackathon":
    case "pitch_competition":
    case "challenge":
    case "remote_cohort_course":
      return "mint";
    default:
      return "lavender";
  }
}

export function toneClasses(
  tone: "lavender" | "mint" | "peach",
): string {
  switch (tone) {
    case "mint":
      return "bg-[color:var(--color-accent-mint)] text-ink";
    case "peach":
      return "bg-[color:var(--color-peach)] text-ink";
    default:
      return "bg-[color:var(--color-accent-lavender)] text-ink";
  }
}

const dayMs = 24 * 60 * 60 * 1000;

export function daysUntil(dateStr: string | null | undefined): number | null {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(d);
  target.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / dayMs);
}

export function formatShortDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

export function formatLongDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function isVerifiedToday(updatedAt: string | null | undefined): boolean {
  if (!updatedAt) return false;
  const d = new Date(updatedAt);
  if (Number.isNaN(d.getTime())) return false;
  return isSameDay(d, new Date());
}

export type DeadlineDisplay = {
  label: string;
  variant: "normal" | "urgent" | "rolling" | "expired";
  daysLeft: number | null;
};

export function deadlineDisplay(g: {
  closing_date: string | null;
  is_rolling: boolean | null;
  status: string | null;
}): DeadlineDisplay {
  if (g.is_rolling) {
    return { label: "Rolling intake", variant: "rolling", daysLeft: null };
  }
  const dl = daysUntil(g.closing_date);
  if (g.status === "expired") {
    return {
      label: `Closed ${formatShortDate(g.closing_date)} · may still accept`,
      variant: "expired",
      daysLeft: dl,
    };
  }
  if (dl == null) {
    return { label: "Deadline TBD", variant: "normal", daysLeft: null };
  }
  const short = formatShortDate(g.closing_date);
  return {
    label: `Closes ${short} · ${dl}d`,
    variant: dl <= 21 ? "urgent" : "normal",
    daysLeft: dl,
  };
}

export function countdownParts(closingDate: string | null | undefined): {
  d: number;
  h: number;
  m: number;
} | null {
  if (!closingDate) return null;
  const target = new Date(closingDate).getTime();
  if (Number.isNaN(target)) return null;
  const diff = target - Date.now();
  if (diff <= 0) return { d: 0, h: 0, m: 0 };
  const d = Math.floor(diff / (24 * 3600 * 1000));
  const h = Math.floor((diff % (24 * 3600 * 1000)) / (3600 * 1000));
  const m = Math.floor((diff % (3600 * 1000)) / (60 * 1000));
  return { d, h, m };
}
